import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import React, { useState, useMemo } from "react";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddIcon from "@mui/icons-material/Add";
import { useGetGreekBoxListQuery, useDeleteGreekBoxMutation, useUpdateGreekBoxMutation } from "../../store/api/commonAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { showToast, showError } from "../../constants/toasts";

const ManageGreekBox = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  // Fetch Greek boxes from API
  const { data, isLoading, error, refetch } = useGetGreekBoxListQuery({
    page,
    limit,
    search: searchTerm,
  });

  // Delete Greek box mutation
  const [deleteGreekBox, { isLoading: isDeleting }] = useDeleteGreekBoxMutation();
  
  // Update Greek box status mutation
  const [updateGreekBox, { isLoading: isUpdatingStatus }] = useUpdateGreekBoxMutation();

  // Store raw API data for edit
  const rawGreekBoxData = useMemo(() => {
    if (data?.data?.greekbox && Array.isArray(data.data.greekbox)) {
      return data.data.greekbox;
    }
    return [];
  }, [data]);

  // Extract Greek boxes from API response
  const greekBoxes = useMemo(() => {
    if (data?.data?.greekbox && Array.isArray(data.data.greekbox)) {
      return data.data.greekbox.map((item: any) => {
        // Extract box art names from boxDetails array
        let boxArtNames = "N/A";
        if (item.boxDetails && Array.isArray(item.boxDetails) && item.boxDetails.length > 0) {
          const names = item.boxDetails
            .map((box: any) => box.name)
            .filter((name: any) => name); // Filter out any undefined/null names
          if (names.length > 0) {
            boxArtNames = names.join(", ");
          }
        } else if (item.boxDetails?.name) {
          // Fallback for single box detail object (legacy)
          boxArtNames = item.boxDetails.name;
        }

        return {
          id: item._id || item.id,
          name: item.name || "N/A",
          boxArt: boxArtNames,
          boxArtImage: item.boxDetails?.[0]?.image || item.boxDetails?.image || "/static/images/placeholder.png",
          flavorMinCount: item.flavorMinCount || 0,
          flavorMaxCount: item.flavorMaxCount || 0,
          toppingMinCount: item.toppingMinCount || 0,
          toppingMaxCount: item.toppingMaxCount || 0,
          price: item.price || 0,
          isActive: item.isActive ?? true,
          featured: item.featured ?? false,
          createdAt: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A",
        };
      });
    }
    return [];
  }, [data]);

  // Calculate total pages
  const totalCount = data?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [greekBoxToDelete, setGreekBoxToDelete] = useState<{ id: string; name: string } | null>(null);

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setGreekBoxToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the Greek box
  const handleConfirmDelete = async () => {
    if (!greekBoxToDelete) return;

    try {
      const response = await deleteGreekBox({ id: greekBoxToDelete.id }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || `Greek Box "${greekBoxToDelete.name}" deleted successfully`;
      showToast(successMessage);
      
      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setGreekBoxToDelete(null);
    } catch (error: any) {
      console.error("Error deleting Greek box:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete Greek box. Please try again.";
      
      showError(errorMessage);
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setGreekBoxToDelete(null);
    }
  };

  // Handle status toggle (isActive)
  const handleStatusToggle = async (id: string, currentStatus: boolean) => {
    try {
      const response = await updateGreekBox({
        id,
        isActive: !currentStatus,
      }).unwrap();

      const successMessage = response?.message || "Status updated successfully";
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating status:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update status. Please try again.";
      showError(errorMessage);
    }
  };

  // Handle featured toggle
  const handleFeaturedToggle = async (id: string, currentFeatured: boolean) => {
    try {
      const response = await updateGreekBox({
        id,
        featured: !currentFeatured,
      }).unwrap();

      const successMessage = response?.message || "Featured status updated successfully";
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating featured status:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update featured status. Please try again.";
      showError(errorMessage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdatingStatus} />
      <div className="main_title">
        <h1>Manage Greek Box</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Greek Box
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Greek Boxes..."
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => navigate("/manage-greek-box/add")}
            >
              <AddIcon /> Add Greek Box
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Box Name</TableCell>
                <TableCell>Box Art</TableCell>
                <TableCell>Flavours Limits</TableCell>
                <TableCell>Toppings Limits</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Featured</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Error loading Greek boxes. Please try again.
                  </TableCell>
                </TableRow>
              ) : greekBoxes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {isLoading ? "Loading..." : "No Greek boxes found"}
                  </TableCell>
                </TableRow>
              ) : (
                greekBoxes.map((box: any, index: number) => (
                  <TableRow hover key={box.id}>
                    <TableCell>{box.name}</TableCell>
                    <TableCell>{box.boxArt}</TableCell>
                    <TableCell>
                      Min: <strong>{box.flavorMinCount}</strong>
                      <br />
                      Max: <strong>{box.flavorMaxCount}</strong>
                    </TableCell>
                    <TableCell>
                      Min: <strong>{box.toppingMinCount}</strong>
                      <br />
                      Max: <strong>{box.toppingMaxCount}</strong>
                    </TableCell>
                    <TableCell>{box.price ? `${box.price.toFixed(3)} KD` : "N/A"}</TableCell>
                    <TableCell>{box.createdAt}</TableCell>
                    <TableCell>
                      <SwitchToggle 
                        checked={box.featured} 
                        onChange={() => {
                          if (box.id) {
                            handleFeaturedToggle(box.id, box.featured);
                          }
                        }}
                        disabled={isUpdatingStatus || !box.id}
                      />
                    </TableCell>
                    <TableCell>
                      <SwitchToggle 
                        checked={box.isActive} 
                        onChange={() => {
                          if (box.id) {
                            handleStatusToggle(box.id, box.isActive);
                          }
                        }}
                        disabled={isUpdatingStatus || !box.id}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton 
                          onClick={() => {
                            const rawItem: any = rawGreekBoxData.find((item: any) => (item._id || item.id) === box.id);
                            navigate("/manage-greek-box/add", { 
                              state: rawItem || {
                                id: box.id,
                                _id: box.id,
                                name: box.name,
                                price: box.price,
                                Description: "",
                                boxId: rawItem?.boxDetails?._id || "",
                                flavorId: rawItem?.flavourDetails?._id || "",
                                toppingId: rawItem?.toppingDetails?._id || "",
                                flavorMinCount: box.flavorMinCount,
                                flavorMaxCount: box.flavorMaxCount,
                                toppingMinCount: box.toppingMinCount,
                                toppingMaxCount: box.toppingMaxCount,
                                isActive: box.isActive,
                                featured: false,
                                image: rawItem?.image || "",
                                sliceImage: rawItem?.sliceImage || "",
                              }
                            });
                          }}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() => box.id && handleDeleteClick(box.id, box.name)}
                          disabled={isDeleting || !box.id}
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 0 && (
          <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
            <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
              Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Pagination
                module={greekBoxes}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                setPage={setPage}
                totalCount={totalCount}
                limit={limit}
              />
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={greekBoxToDelete?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageGreekBox;
